<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <v-toolbar rounded flat color="transparent">
          <v-toolbar-title>
            <h2 class="h2">Ma saisie de données</h2>
          </v-toolbar-title>
          <v-divider vertical class="mx-5"></v-divider>
          <v-btn @click="init" color="primary">Revenir à la saisie en cours</v-btn>
          <v-spacer></v-spacer>
          <GeneratorYears
            v-slot:default="{years}"
            :startDate="firstEntryDate"
            :endDate="lastEntryDate"
          >
            <v-select
              class="mr-5"
              v-model="selectedYear"
              :items="years.reverse()"
              :label="$t('Calendar.Label.year')"
              hide-details
              style="max-width: 125px;"
            ></v-select>
          </GeneratorYears>

          <GeneratorMonths
            v-if="inputFrequency === frequencyKeys.MENSUEL"
            v-slot:default="{months}"
            :startDate="firstEntryDate"
            :endDate="lastEntryDate"
            :year="selectedYear"
          >
            <v-select
              v-model="selectedMonth"
              :items="months"
              :label="$t('Calendar.Label.month')"
              hide-details
              class="mr-5"
              style="max-width: 125px;"
            ></v-select>
          </GeneratorMonths>

          <GeneratorWeeks
            v-if="inputFrequency === frequencyKeys.QUOTIDIEN || inputFrequency === frequencyKeys.HEBDOMADAIRE"
            v-slot:default="{weeks}"
            :startDate="firstEntryDate"
            :endDate="lastEntryDate"
            :year="selectedYear"
          >
            <v-select
              v-model="selectedWeek"
              :items="weeks"
              :label="$t('Calendar.Label.week')"
              hide-details
              style="max-width: 125px;"
            ></v-select>
          </GeneratorWeeks>
        </v-toolbar>
      </v-col>
    </v-row>
    <v-slide-x-transition>
      <v-row align="center" v-if="!loading && entries.length && (selectedMonth || selectedWeek)">
        <v-col cols="12">
          <EntryForm
            :entries="entries"
            :frequency="inputFrequency"
            :user="user"
            @sent="init"
          />
        </v-col>
      </v-row>
    </v-slide-x-transition>
  </v-container>
</template>

<script>
import { frequencyKeys } from '@/config/fixedValues';
import { mapState } from 'vuex'
import Entries from '@/services/entries'
import EntryForm from '@/components/Entry/EntryForm'
import GeneratorMonths from '@/components/Utils/GeneratorMonths'
import GeneratorWeeks from '@/components/Utils/GeneratorWeeks'
import GeneratorYears from '@/components/Utils/GeneratorYears'
import { endOfWeek, parseISO, formatISO, getYear, startOfMonth, startOfWeek } from 'date-fns'

export default {
  name: 'MyEntries',
  components: { EntryForm, GeneratorMonths, GeneratorWeeks, GeneratorYears },
  data: () => ({
    frequencyKeys,
    entries: [],
    selectedYear: null,
    selectedMonth: null,
    selectedWeek: null,
    lastEntryDate: new Date(),
    loading: true
  }),
  computed: {
    ...mapState('auth', [
      'user'
    ]),

    inputFrequency () {
      return this.user.inputFrequency
    },

    firstEntryDate () {
      const openingDate = parseISO(this.user.openingDate)
      return this.inputFrequency === frequencyKeys.MENSUEL ? startOfMonth(openingDate) : startOfWeek(openingDate, {weekStartsOn: 1})
    }
  },
  watch: {
    async selectedWeek (value) {
      if (value) {
        this.loading = true
        this.loadEntries(await this.getEntries(value))
        this.loading = false
      }
    },
    async selectedMonth (value) {
      if (value) {
        this.loading = true
        this.loadEntries(await this.getEntries(value))
        this.loading = false
      }
    },
    selectedYear (value, oldValue) {
      if (oldValue) {
        this.selectedWeek = null
        this.selectedMonth = null
      }
    }
  },

  methods: {
    async init () {
      this.loading = true
      try {
        let currentEntries = await Entries.getCurrent(this.user.id)

        if (!currentEntries.length) {
          currentEntries = await Entries.generateNext(this.user.id)
        }

        this.loadEntries(currentEntries)

        this.lastEntryDate = this.entries[0].dateISO
        this.selectedYear = getYear(this.entries[0].dateISO)
        if (this.inputFrequency === frequencyKeys.MENSUEL) this.selectedMonth = this.entries[0].dateISO
        if (this.inputFrequency === frequencyKeys.QUOTIDIEN || this.inputFrequency === frequencyKeys.HEBDOMADAIRE) this.selectedWeek = this.entries[0].dateISO
        this.loading = false
      } catch (error) {
        this.$store.dispatch('errorMessage', 'Erreur lors de la récupération de la saisie courante')
      }
    },

    /**
     * Get data for given date
     */
    async getEntries (startDate) {
      const endDate = this.inputFrequency === frequencyKeys.QUOTIDIEN ? endOfWeek(startDate, {weekStartsOn: 1}) : startDate
      return await Entries.getUserEntriesByPeriod(this.user.id, {
        startDate: formatISO(startDate, { representation: 'date' }),
        endDate: formatISO(endDate, { representation: 'date' })
      })
    },

  /**
   * Hydrate entries before loading them in component
   */
    loadEntries (entries) {
      this.entries = entries.map(entry => ({
        ...entry,
        dateISO: parseISO(entry.date),
        disabled: !entry.isUserOpen || entry.hasBeenSent,
        canComment: !entry.hasBeenSent
      }))
    },

  },
  created () {
    this.init()
  }
}
</script>
